import {
  Component,
  inject,
  input,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { SetUpTwoFactorScreen } from '../set-up-two-factor.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MfaService } from '../../../core/http/mfa.service';
import { first, Observable, of, takeWhile, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButton } from '@angular/material/button';
import {
  MatError,
  MatFormField,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MfaFailComponent } from '../../../components/mfa-fail/mfa-fail.component';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MfaErrors } from '../../../core/state/mfa/mfa.state';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-set-up-email',
  imports: [
    MatButton,
    MatError,
    MatInput,
    MatFormField,
    ReactiveFormsModule,
    TranslateModule,
    MfaFailComponent,
    AsyncPipe,
    DatePipe,
    MatIcon,
    MatSuffix,
    MatProgressSpinner,
  ],
  templateUrl: './set-up-email.component.html',
  styleUrls: [
    '../../login-form/login-form.component.scss',
    '../set-up-two-factor.component.scss',
  ],
})
export class SetUpEmailComponent implements OnInit {
  dismiss: OutputEmitterRef<void> = output<void>();
  changeScreen: OutputEmitterRef<SetUpTwoFactorScreen> =
    output<SetUpTwoFactorScreen>();
  resetTimer: OutputEmitterRef<void> = output<void>();
  otpForm: FormGroup;
  email: Signal<string> = input();

  wasEmailSent = false;
  millisecondsRemaining$: Observable<number>;
  hasFailed = false;
  isLoading = false;

  private mfaService = inject(MfaService);
  private fb = inject(FormBuilder);

  ngOnInit() {
    this.otpForm = this.fb.group({
      mfaCode: new FormControl('', [
        Validators.required,
        Validators.pattern('[0-9]{6}'),
      ]),
    });
  }

  onSendEmail() {
    this.mfaService
      .sendSetUpEmailCode()
      .pipe(first())
      .subscribe(() => {
        this.resetTimer.emit();
        this.wasEmailSent = true;
        const endTime = Date.now() + 300000; // 5 minutes from now

        this.millisecondsRemaining$ = timer(0, 100).pipe(
          map(() => {
            const remaining = endTime - Date.now();
            return Math.max(0, remaining);
          }),
          takeWhile((remaining) => remaining > 0, true),
        );
      });
  }

  onActivate() {
    this.isLoading = true;
    const mfaCode = this.otpForm.get('mfaCode');
    this.mfaService
      .setUpEmailMfa(mfaCode?.value)
      .pipe(first())
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.isLoading = false;
          if (error.status === 417) {
            mfaCode.setErrors({ incorrect: true });
          } else {
            this.hasFailed = true;
          }
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.isLoading = false;
        if (res) {
          this.changeScreen.emit(SetUpTwoFactorScreen.FINAL);
        }
      });
  }

  onDismiss() {
    this.dismiss.emit();
  }

  protected readonly MfaErrors = MfaErrors;
}
